import React, { useState } from 'react';
import { CheckCircle2Icon, LockKeyholeIcon, MenuIcon, Settings2Icon } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { BudgetAllocationsTab } from '../components/settings/BudgetAllocationsTab';
import { CatalogDrawer } from '../components/settings/CatalogDrawer';
import { CatalogManagementTab } from '../components/settings/CatalogManagementTab';
import { UserDrawer } from '../components/settings/UserDrawer';
import { UsersTab } from '../components/settings/UsersTab';
import { AppUser, settingsActions } from '../data/settingsStore';
import { Sidebar } from '../components/Sidebar';
import { useScreenInit } from '../useScreenInit';
type SettingsTab = 'departments' | 'budget' | 'users';
export function Settings() {
  useScreenInit();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isAdmin] = useState(true);
  const [activeTab, setActiveTab] = useState<SettingsTab>('departments');
  const [editingUser, setEditingUser] = useState<AppUser | null>(null);
  const [isNewUserOpen, setIsNewUserOpen] = useState(false);
  const [isDepartmentDrawerOpen, setIsDepartmentDrawerOpen] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState('');
  const closeUserDrawer = () => {
    setEditingUser(null);
    setIsNewUserOpen(false);
  };
  if (!isAdmin) {
    return <Shell sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} isAdmin={isAdmin} data-id="element-2012" data-mp="3t5azhfakjn9j">
        <div className="mx-auto max-w-sm text-center" data-id="element-2013" data-mp="auz7va8hrdyn">
          <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-slate-500" data-id="element-2014" data-mp="5srucg80jpu7">
            <LockKeyholeIcon className="h-6 w-6" data-id="element-2015" data-mp="kqaro6yrbbd9" />
          </span>
          <h1 className="text-xl font-semibold text-slate-900" data-id="element-2016" data-mp="frpmdt4ne1hy">
            Settings restricted
          </h1>
          <p className="mt-2 text-sm text-slate-500" data-id="element-2017" data-mp="2mj8dhiu493op">
            Settings access is limited to administrators.
          </p>
        </div>
      </Shell>;
  }
  return <Shell sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} isAdmin={isAdmin} data-id="element-2018" data-mp="377zsnysju94v">
      <div className="flex h-full w-full min-w-0 flex-1 flex-col overflow-hidden bg-white md:rounded-2xl md:shadow-xl" data-id="element-2019" data-mp="3b8be3dsqkdhe">
        <header className="border-b border-slate-100 px-5 py-5 md:px-8 md:py-7" data-id="element-2020" data-mp="3g7ef8n5lhzk3">
          <button type="button" onClick={() => setSidebarOpen(true)} className="mb-4 flex items-center gap-2 text-[#14466B] md:hidden" aria-label="Open menu" data-id="element-2021" data-mp="3l8akp55lhtrm">
            <MenuIcon className="h-6 w-6" data-id="element-2022" data-mp="3u6e6m2khssjy" />
          </button>
          <div className="flex items-start gap-3" data-id="element-2023" data-mp="2srnynnd3b6k">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-[#14466B]" data-id="element-2024" data-mp="2u1lnbqayhco">
              <Settings2Icon className="h-5 w-5" data-id="element-2025" data-mp="7u790fpjhhoy" />
            </span>
            <div data-id="element-2026" data-mp="ct0mi84tt8p4">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-2027" data-mp="cpcsz8zdsmae">Settings</h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-2028" data-mp="hr25kt29580l">
                Manage the app’s operational settings and access.
              </p>
            </div>
          </div>
        </header>

        <div className="border-b border-slate-100 px-5 md:px-8" data-id="element-2029" data-mp="1x8ozo5j2wrap">
          <nav className="flex gap-1 overflow-x-auto" aria-label="Settings sections" data-id="element-2030" data-mp="1sa5ew8g81ljx">
            <TabButton active={activeTab === 'departments'} onClick={() => setActiveTab('departments')} label="Departments" data-id="element-2031" data-mp="1ncdlf9zxhrdm" />
            <TabButton active={activeTab === 'budget'} onClick={() => setActiveTab('budget')} label="Budget" data-id="element-2032" data-mp="2fzg54vzy3xjt" />
            <TabButton active={activeTab === 'users'} onClick={() => setActiveTab('users')} label="Users" data-id="element-2033" data-mp="2b76l1wlihrpg" />
          </nav>
        </div>

        <main className="flex-1 overflow-auto px-5 py-6 md:px-8 md:py-7" data-id="element-2034" data-mp="33rn6yaofwwqm">
          {inviteSuccess && <div className="mb-5 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800" role="status" data-id="element-2035" data-mp="2ywr5yl186z1d">
              <CheckCircle2Icon className="mt-0.5 h-5 w-5 flex-shrink-0" data-id="element-2036" data-mp="2oh8f3d4tegec" />
              <div data-id="element-2037" data-mp="29jy4s73wrgk7">
                <p className="text-sm font-semibold" data-id="element-2038" data-mp="29khampgaaehv">
                  Invite sent to {inviteSuccess}
                </p>
                <p className="mt-0.5 text-sm" data-id="element-2039" data-mp="2ejkfo9w9kkpu">
                  They’ll set their password when they log in for the first
                  time.
                </p>
              </div>
              <button type="button" onClick={() => setInviteSuccess('')} className="ml-auto text-sm font-medium hover:underline" aria-label="Dismiss invite confirmation" data-id="element-2040" data-mp="378hmngv0yv3a">
                Dismiss
              </button>
            </div>}
          {activeTab === 'departments' && <CatalogManagementTab onAdd={() => setIsDepartmentDrawerOpen(true)} data-id="element-2041" data-mp="3pxoatjel8ewh" />}
          {activeTab === 'budget' && <BudgetAllocationsTab data-id="element-2042" data-mp="1eg2ujmpki874" />}
          {activeTab === 'users' && <UsersTab onNewUser={() => setIsNewUserOpen(true)} onEditUser={user => setEditingUser(user)} data-id="element-2043" data-mp="19i1gxhawv131" />}
        </main>
      </div>

      <AnimatePresence data-id="element-2044" data-mp="1j9p0yx551fuh">
        {(isNewUserOpen || editingUser) && <UserDrawer user={editingUser || undefined} onClose={closeUserDrawer} onSave={user => {
        if (editingUser) {
          settingsActions.updateUser(editingUser.id, user);
        } else {
          settingsActions.addUser(user);
          setInviteSuccess(user.email);
        }
        closeUserDrawer();
      }} data-id="element-2045" data-mp="vitbi6o5mwqe" />}
        {isDepartmentDrawerOpen && <CatalogDrawer onClose={() => setIsDepartmentDrawerOpen(false)} data-id="element-2046" data-mp="2g0zn1j4jd0hd" />}
      </AnimatePresence>
    </Shell>;
}
function Shell({
  sidebarOpen,
  setSidebarOpen,
  isAdmin,
  children
}: {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  isAdmin: boolean;
  children: React.ReactNode;
}) {
  return <div className="flex h-screen w-screen overflow-hidden font-sans" style={{
    background: 'linear-gradient(to bottom, #16486B 0%, #0F3D5C 45%, #082A40 100%)'
  }} data-id="element-2047" data-mp="1ikuzubjz55sr">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isAdmin={isAdmin} data-id="element-2048" data-mp="pwfuyhuk43v5" />
      <main className="flex min-w-0 flex-1 overflow-hidden p-0 md:py-3 md:pr-3" data-id="element-2049" data-mp="uvsht2lfhaxq">
        {children}
      </main>
    </div>;
}
function TabButton({
  active,
  onClick,
  label
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return <button type="button" role="tab" aria-selected={active} onClick={onClick} className={`relative whitespace-nowrap px-4 py-3 text-sm font-medium transition-colors ${active ? 'text-[#14466B]' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2050" data-mp="75emn37jqvyx">
      {label}
      {active && <span className="absolute inset-x-4 bottom-0 h-0.5 bg-[#14466B]" data-id="element-2051" data-mp="3uu1up1fsmug5" />}
    </button>;
}